import { CloudUpload as UploadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, IconButton, Tooltip, Chip, Collapse, InputAdornment, ButtonGroup,
  LinearProgress, Divider, Stack
} from '@mui/material';
import {
  Add as AddIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { Autocomplete } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'Cheque', 'UPI'];

const VendorPaymentsTab = ({ selectedSiteId, sites, userRole }) => {
  const [allSites, setAllSites] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [vendorDetail, setVendorDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    supplierName: '', billNumber: '', billType: 'GST', amountPaid: '', paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: '', referenceNo: '', notes: ''
  });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [applyAdvanceOpen, setApplyAdvanceOpen] = useState(false);
  const [applyAdvanceForm, setApplyAdvanceForm] = useState({ billNumber: '', amount: '' });
  const [applyAdvanceSubmitting, setApplyAdvanceSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkIncludeDuplicates, setBulkIncludeDuplicates] = useState({});
  const [bulkResult, setBulkResult] = useState(null);

  const canManage = userRole === 'admin' || userRole === 'manager';
  const effectiveSiteId = allSites ? 'all' : selectedSiteId;
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchVendors = useCallback(async () => {
    if (!effectiveSiteId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/vendor-payments/outstanding-summary`, {
        ...authHeaders(), params: { siteId: effectiveSiteId }
      });
      setVendors(res.data.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load outstanding summary');
    } finally {
      setLoading(false);
    }
  }, [effectiveSiteId]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const [allVendorNames, setAllVendorNames] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/vendors`, authHeaders())
      .then(res => {
        const names = (res.data.vendors || res.data.data?.vendors || []).map(v => v.vendorName);
        setAllVendorNames(names);
      })
      .catch(() => { }); // non-critical — dropdown still works with billed vendors if this fails
  }, []);

  const fetchVendorDetail = async (supplierName) => {
    if (!selectedSiteId) return;
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API_BASE_URL}/vendor-payments/vendor-detail`, {
        ...authHeaders(), params: { siteId: selectedSiteId, supplierName }
      });
      setVendorDetail(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vendor detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExpand = (supplierName) => {
    if (expandedVendor === supplierName) {
      setExpandedVendor(null);
      setVendorDetail(null);
    } else {
      setExpandedVendor(supplierName);
      fetchVendorDetail(supplierName);
    }
  };

  const openPaymentDialog = (supplierName = '', billNumber = '') => {
    setEditingPaymentId(null);
    setPaymentForm({
      supplierName, billNumber: billNumber || '', billType: 'GST', amountPaid: '',
      paymentDate: new Date().toISOString().split('T')[0], paymentMode: '', referenceNo: '', notes: ''
    });
    setPaymentOpen(true);
  };

  const openEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setPaymentForm({
      supplierName: payment.supplierName, billNumber: payment.billNumber || '', billType: payment.billType || 'Non-GST',
      amountPaid: payment.amountPaid, paymentDate: payment.paymentDate,
      paymentMode: payment.paymentMode || '', referenceNo: payment.referenceNo || '', notes: payment.notes || ''
    });
    setPaymentOpen(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentForm.supplierName || !paymentForm.amountPaid || !paymentForm.paymentDate) {
      setError('Vendor, amount and date are required');
      return;
    }
    try {
      setPaymentSubmitting(true);
      if (editingPaymentId) {
        await axios.put(`${API_BASE_URL}/vendor-payments/${editingPaymentId}`, {
          billNumber: paymentForm.billNumber || null,
          billType: paymentForm.billType,
          amountPaid: paymentForm.amountPaid,
          paymentDate: paymentForm.paymentDate,
          paymentMode: paymentForm.paymentMode || null,
          referenceNo: paymentForm.referenceNo || null,
          notes: paymentForm.notes || null
        }, authHeaders());
        setSuccess('Payment updated');
      } else {
        await axios.post(`${API_BASE_URL}/vendor-payments`, {
          siteId: selectedSiteId,
          supplierName: paymentForm.supplierName,
          billNumber: paymentForm.billNumber || null,
          billType: paymentForm.billType,
          amountPaid: paymentForm.amountPaid,
          paymentDate: paymentForm.paymentDate,
          paymentMode: paymentForm.paymentMode || null,
          referenceNo: paymentForm.referenceNo || null,
          notes: paymentForm.notes || null
        }, authHeaders());
        setSuccess('Payment recorded');
      }
      setPaymentOpen(false);
      fetchVendors();
      if (expandedVendor) fetchVendorDetail(expandedVendor);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await axios.delete(`${API_BASE_URL}/vendor-payments/${paymentId}`, authHeaders());
      setSuccess('Payment deleted');
      fetchVendors();
      if (expandedVendor) fetchVendorDetail(expandedVendor);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete payment');
    }
  };

  const openApplyAdvance = (billNumber, maxAmount) => {
    setApplyAdvanceForm({ billNumber, amount: maxAmount });
    setApplyAdvanceOpen(true);
  };

  const handleApplyAdvance = async () => {
    try {
      setApplyAdvanceSubmitting(true);
      await axios.post(`${API_BASE_URL}/vendor-payments/apply-advance`, {
        siteId: selectedSiteId,
        supplierName: expandedVendor,
        billNumber: applyAdvanceForm.billNumber,
        amount: applyAdvanceForm.amount
      }, authHeaders());
      setSuccess('Advance applied to bill');
      setApplyAdvanceOpen(false);
      fetchVendors();
      if (expandedVendor) fetchVendorDetail(expandedVendor);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply advance');
    } finally {
      setApplyAdvanceSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vendor-payments/bulk-template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Vendor_Payments_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vendor-payments/export/excel`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { siteId: effectiveSiteId },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vendor_Payments_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download report');
    }
  };

  const openBulkDialog = () => {
    setBulkFile(null);
    setBulkPreviewData(null);
    setBulkIncludeDuplicates({});
    setBulkResult(null);
    setBulkOpen(true);
  };

  const handleBulkPreview = async () => {
    if (!bulkFile) return;
    try {
      setBulkPreviewLoading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await axios.post(`${API_BASE_URL}/vendor-payments/bulk-preview`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBulkPreviewData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to preview file');
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const toggleIncludeDuplicate = (rowNumber) => {
    setBulkIncludeDuplicates(prev => ({ ...prev, [rowNumber]: !prev[rowNumber] }));
  };

  const handleBulkImport = async () => {
    if (!bulkPreviewData) return;
    const rowsToImport = bulkPreviewData.rows.filter(r =>
      r.status === 'valid' && (!r.isDuplicate || bulkIncludeDuplicates[r.rowNumber])
    );
    if (rowsToImport.length === 0) {
      setError('No valid rows to import');
      return;
    }
    try {
      setBulkImporting(true);
      setError('');
      const res = await axios.post(`${API_BASE_URL}/vendor-payments/bulk-import`, { rows: rowsToImport }, authHeaders());
      setBulkResult(res.data.data);
      setSuccess(res.data.message);
      fetchVendors();
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  const filteredVendors = vendors.filter(v =>
    !searchQuery.trim() || v.supplierName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totals = filteredVendors.reduce((acc, v) => ({
    billed: acc.billed + v.totalBilled,
    paid: acc.paid + v.totalPaid,
    outstanding: acc.outstanding + Math.max(v.outstanding, 0),
    advance: acc.advance + Math.max(-v.outstanding, 0)
  }), { billed: 0, paid: 0, outstanding: 0, advance: 0 });

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1, borderLeft: '4px solid', borderColor: 'grey.400' }}>
          <Typography variant="caption" color="text.secondary">Total Billed</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>₹{totals.billed.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: '4px solid', borderColor: 'success.main' }}>
          <Typography variant="caption" color="text.secondary">Total Paid</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>₹{totals.paid.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="caption" color="text.secondary">Outstanding (You Owe)</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.dark' }}>₹{totals.outstanding.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Typography variant="caption" color="text.secondary">Unapplied Advance</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.dark' }}>₹{totals.advance.toLocaleString()}</Typography>
        </Paper>
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ButtonGroup size="small">
            <Button variant={!allSites ? 'contained' : 'outlined'} onClick={() => setAllSites(false)}>This Site</Button>
            <Button variant={allSites ? 'contained' : 'outlined'} onClick={() => setAllSites(true)}>All Sites</Button>
          </ButtonGroup>
          <Typography variant="body2" color="text.secondary">
            {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small" placeholder="Search vendor..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              )
            }}
          />
          {canManage && (
            <>
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={openBulkDialog}>
                Bulk Upload
              </Button>
              <Button variant="outlined" onClick={handleDownloadReport}>
                Download Report
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openPaymentDialog()} disabled={allSites}>
                Record Payment
              </Button>
            </>
          )}
        </Box>
      </Box>

      {allSites && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing outstanding across all sites. Switch to "This Site" to record or drill into individual bill payments.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Vendor</TableCell>
              <TableCell align="right">Total Billed</TableCell>
              <TableCell align="right">Total Paid</TableCell>
              <TableCell align="right">Outstanding</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
            ) : filteredVendors.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No vendors found</TableCell></TableRow>
            ) : (
              filteredVendors.map((v) => (
                <React.Fragment key={v.supplierName}>
                  <TableRow hover sx={{ cursor: allSites ? 'default' : 'pointer' }}
                    onClick={() => !allSites && handleExpand(v.supplierName)}>
                    <TableCell>
                      {!allSites && (
                        <IconButton size="small">
                          {expandedVendor === v.supplierName ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.supplierName}</Typography>
                      {v.totalBilled > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((v.totalPaid / v.totalBilled) * 100, 100)}
                          sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                          color={v.outstanding > 0 ? 'warning' : 'success'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">₹{v.totalBilled.toLocaleString()}</TableCell>
                    <TableCell align="right">₹{v.totalPaid.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={v.outstanding > 0 ? `₹${v.outstanding.toLocaleString()} due` : v.outstanding < 0 ? `₹${Math.abs(v.outstanding).toLocaleString()} credit` : 'Settled'}
                        size="small"
                        color={v.outstanding > 0 ? 'warning' : v.outstanding < 0 ? 'info' : 'success'} />
                    </TableCell>
                  </TableRow>
                  {!allSites && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: expandedVendor === v.supplierName ? undefined : 0 }}>
                        <Collapse in={expandedVendor === v.supplierName} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            {detailLoading ? (
                              <CircularProgress size={24} />
                            ) : vendorDetail && vendorDetail.supplierName === v.supplierName ? (
                              <>
                                <Typography variant="subtitle2" gutterBottom>Bills</Typography>
                                <Table size="small" sx={{ mb: 2 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Bill Number</TableCell>
                                      <TableCell align="right">Billed</TableCell>
                                      <TableCell align="right">Paid</TableCell>
                                      <TableCell align="right">Outstanding</TableCell>
                                      {canManage && <TableCell align="center">Action</TableCell>}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {vendorDetail.bills.map((b) => {
                                      const canApplyAdvance = b.billNumber && b.outstanding > 0 && vendorDetail.unallocatedPaid > 0;
                                      return (
                                        <TableRow key={b.billNumber || 'NO_BILL'}
                                          sx={canApplyAdvance ? { bgcolor: '#e3f2fd' } : undefined}>
                                          <TableCell>{b.billNumber || <Chip label="No Bill Number Yet" size="small" variant="outlined" />}</TableCell>
                                          <TableCell align="right">₹{b.billed.toLocaleString()}</TableCell>
                                          <TableCell align="right">₹{b.paid.toLocaleString()}</TableCell>
                                          <TableCell align="right">₹{b.outstanding.toLocaleString()}</TableCell>
                                          {canManage && (
                                            <TableCell align="center">
                                              <Stack direction="row" spacing={1} justifyContent="center">
                                                {b.billNumber && (
                                                  <Button size="small" variant="outlined" onClick={() => openPaymentDialog(v.supplierName, b.billNumber)}>Pay</Button>
                                                )}
                                                {canApplyAdvance && (
                                                  <Button size="small" variant="contained" color="info"
                                                    onClick={() => openApplyAdvance(b.billNumber, Math.min(b.outstanding, vendorDetail.unallocatedPaid))}>
                                                    Apply Advance
                                                  </Button>
                                                )}
                                              </Stack>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                                {vendorDetail.unallocatedPaid > 0 && (
                                  <Alert severity="info" sx={{ mb: 2 }}>
                                    ₹{vendorDetail.unallocatedPaid.toLocaleString()} paid as advance, not yet applied to a specific bill.
                                  </Alert>
                                )}
                                <Typography variant="subtitle2" gutterBottom>Payment History</Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Date</TableCell>
                                      <TableCell>Bill</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell align="right">Amount</TableCell>
                                      <TableCell>Mode</TableCell>
                                      <TableCell>Reference</TableCell>
                                      <TableCell>Notes</TableCell>
                                      {canManage && <TableCell align="center">Actions</TableCell>}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {vendorDetail.payments.length === 0 ? (
                                      <TableRow><TableCell colSpan={canManage ? 8 : 7} align="center">No payments recorded yet</TableCell></TableRow>
                                    ) : (
                                      vendorDetail.payments.map((p) => (
                                        <TableRow key={p.id}>
                                          <TableCell>{new Date(p.paymentDate).toLocaleDateString()}</TableCell>
                                          <TableCell>
                                            {p.billNumber ? p.billNumber : (
                                              p.billType === 'GST'
                                                ? <Chip label="Bill Pending" size="small" color="warning" />
                                                : <Chip label="Advance" size="small" variant="outlined" />
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Chip label={p.billType || 'Non-GST'} size="small" variant="outlined"
                                              color={p.billType === 'GST' ? 'primary' : 'default'} />
                                          </TableCell>
                                          <TableCell align="right">₹{Number(p.amountPaid).toLocaleString()}</TableCell>
                                          <TableCell>{p.paymentMode || '-'}</TableCell>
                                          <TableCell>{p.referenceNo || '-'}</TableCell>
                                          <TableCell>{p.notes || '-'}</TableCell>
                                          {canManage && (
                                            <TableCell align="center">
                                              <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEditPayment(p)}><EditIcon fontSize="small" /></IconButton>
                                              </Tooltip>
                                              <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => handleDeletePayment(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                                              </Tooltip>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </>
                            ) : null}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
          {filteredVendors.length > 0 && (
            <TableBody>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell />
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>₹{totals.billed.toLocaleString()}</strong></TableCell>
                <TableCell align="right"><strong>₹{totals.paid.toLocaleString()}</strong></TableCell>
                <TableCell align="right"><strong>₹{totals.outstanding.toLocaleString()}</strong></TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingPaymentId ? (
              <TextField label="Vendor" value={paymentForm.supplierName} disabled fullWidth />
            ) : (
              <Autocomplete
                freeSolo
                options={[...new Set([...allVendorNames, ...vendors.map(v => v.supplierName)])].sort()}
                value={paymentForm.supplierName}
                onInputChange={(e, newValue) => setPaymentForm(prev => ({ ...prev, supplierName: newValue, billNumber: '' }))}
                renderInput={(params) => <TextField {...params} label="Vendor *" fullWidth />}
              />
            )}
            <ButtonGroup size="small" sx={{ alignSelf: 'flex-start' }}>
              <Button variant={paymentForm.billType === 'GST' ? 'contained' : 'outlined'}
                onClick={() => setPaymentForm(prev => ({ ...prev, billType: 'GST' }))}>GST</Button>
              <Button variant={paymentForm.billType === 'Non-GST' ? 'contained' : 'outlined'}
                onClick={() => setPaymentForm(prev => ({ ...prev, billType: 'Non-GST' }))}>Non-GST</Button>
            </ButtonGroup>
            <TextField label="Bill Number (leave blank for advance payment)" value={paymentForm.billNumber}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, billNumber: e.target.value }))} fullWidth
              helperText={paymentForm.billType === 'GST'
                ? "Leave blank if the bill isn't available yet — this payment will show as \"Bill Pending\" until you add it (via Edit)"
                : "Leave blank if this payment isn't against a specific bill yet"} />
            <TextField type="number" label="Amount Paid *" value={paymentForm.amountPaid}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, amountPaid: e.target.value }))} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            <TextField type="date" label="Payment Date *" value={paymentForm.paymentDate}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))} fullWidth
              InputLabelProps={{ shrink: true }} />
            <TextField select label="Payment Mode" value={paymentForm.paymentMode} fullWidth
              onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMode: e.target.value }))}>
              {PAYMENT_MODES.map(m => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
            </TextField>
            <TextField label="Reference No. (Cheque/UTR/Txn ID)" value={paymentForm.referenceNo}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, referenceNo: e.target.value }))} fullWidth />
            <TextField label="Notes" value={paymentForm.notes} multiline rows={2}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePaymentSubmit} disabled={paymentSubmitting}>
            {paymentSubmitting ? 'Saving...' : (editingPaymentId ? 'Save Changes' : 'Record Payment')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={applyAdvanceOpen} onClose={() => setApplyAdvanceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply Advance to Bill {applyAdvanceForm.billNumber}</DialogTitle>
        <DialogContent>
          <TextField
            type="number" label="Amount to Apply" fullWidth sx={{ mt: 1 }}
            value={applyAdvanceForm.amount}
            onChange={(e) => setApplyAdvanceForm(prev => ({ ...prev, amount: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            helperText={`Available advance: ₹${vendorDetail?.unallocatedPaid?.toLocaleString() || 0}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyAdvanceOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyAdvance} disabled={applyAdvanceSubmitting}>
            {applyAdvanceSubmitting ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Bulk Upload Payments</DialogTitle>
        <DialogContent>
          {!bulkPreviewData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info">
                Upload an Excel file with columns: <strong>Date, Site, Vendor, Bill Number, Bill Type (GST / Non-GST), Amount, Mode, Reference, Notes</strong>.
                Leave Bill Number blank for a vendor-level advance payment. Site and Vendor must match names already in the system exactly
                (a new Vendor name is fine and will be added automatically).
              </Alert>
              <Button variant="text" onClick={handleDownloadTemplate} sx={{ alignSelf: 'flex-start' }}>
                Download Template
              </Button>
              <Button variant="outlined" component="label">
                {bulkFile ? bulkFile.name : 'Choose Excel File'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files[0] || null)}
                />
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip label={`${bulkPreviewData.totalRows} total`} />
                <Chip label={`${bulkPreviewData.validRows} valid`} color="success" />
                <Chip label={`${bulkPreviewData.errorRows} errors`} color="error" />
                <Chip label={`${bulkPreviewData.duplicateRows} possible duplicates`} color="warning" />
              </Stack>
              {bulkResult ? (
                <Alert severity="success">
                  Imported {bulkResult.imported} payment(s).
                  {bulkResult.failed?.length > 0 && ` ${bulkResult.failed.length} row(s) failed at import time.`}
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 450 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Site</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Bill Number</TableCell>
                        <TableCell>Bill Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Include</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkPreviewData.rows.map((r) => (
                        <TableRow key={r.rowNumber} sx={r.status === 'error' ? { bgcolor: '#ffebee' } : r.isDuplicate ? { bgcolor: '#fff8e1' } : undefined}>
                          <TableCell>{r.rowNumber}</TableCell>
                          <TableCell>{r.date || '-'}</TableCell>
                          <TableCell>{r.siteName || '-'}</TableCell>
                          <TableCell>
                            {r.vendorName || '-'}
                            {r.isNewVendor && <Chip label="New" size="small" color="info" sx={{ ml: 0.5 }} />}
                          </TableCell>
                          <TableCell>{r.billNumber || <Chip label="Advance" size="small" variant="outlined" />}</TableCell>
                          <TableCell>{r.billType || '-'}</TableCell>
                          <TableCell align="right">{r.amount ? `₹${r.amount.toLocaleString()}` : '-'}</TableCell>
                          <TableCell>
                            {r.status === 'error' ? (
                              <Box>
                                <Chip label="Error" size="small" color="error" sx={{ mb: 0.5 }} />
                                <Typography variant="caption" color="error.main" display="block">
                                  {r.errors.join('; ')}
                                </Typography>
                              </Box>
                            ) : r.isDuplicate ? (
                              <Chip label="Possible Duplicate" size="small" color="warning" />
                            ) : (
                              <Chip label="Valid" size="small" color="success" />
                            )}
                          </TableCell>
                          <TableCell>
                            {r.status === 'valid' && r.isDuplicate && (
                              <input
                                type="checkbox"
                                checked={!!bulkIncludeDuplicates[r.rowNumber]}
                                onChange={() => toggleIncludeDuplicate(r.rowNumber)}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Close</Button>
          {!bulkPreviewData && (
            <Button variant="contained" onClick={handleBulkPreview} disabled={!bulkFile || bulkPreviewLoading}>
              {bulkPreviewLoading ? 'Reading File...' : 'Preview'}
            </Button>
          )}
          {bulkPreviewData && !bulkResult && (
            <Button variant="contained" onClick={handleBulkImport} disabled={bulkImporting}>
              {bulkImporting ? 'Importing...' : `Confirm Import (${bulkPreviewData.rows.filter(r => r.status === 'valid' && (!r.isDuplicate || bulkIncludeDuplicates[r.rowNumber])).length})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorPaymentsTab;